/* eslint-disable max-lines -- one message catalog object (commonTexts) */
import { InvoiceStatusStatus } from '@navio/nsdk';
import {
  defaultPriceListContextBannerTexts,
  defaultPriceListDiscountLabelTexts
} from '../booking/catalogPriceDiscount';
import { pluralizeWithValue } from '../string/string';

export const getInvoiceStatusTranslation = (status: InvoiceStatusStatus) => {
  switch (status) {
    case InvoiceStatusStatus.DRAFT:
      return 'Draft';
    case InvoiceStatusStatus.SENT:
      return 'Sent';
    case InvoiceStatusStatus.PAID:
      return 'Paid';
    case InvoiceStatusStatus.CANCELED:
      return 'Canceled';
    default:
      return 'Unknown';
  }
};

export const commonTexts = {
  whatsNewHistory: {
    title: "What's new",
    description: 'All updates, newest first.',
    close: 'Close'
  },
  ordersReceipts: {
    downloadReceipt: 'Download receipt',
    noReceiptsFound: 'No receipts found for this order'
  },
  addressSearch: {
    noAddressesFound: 'No addresses found',
    typeToSearchAddresses: 'Type to search addresses...',
    searchForAddress: 'Search for address...'
  },
  placeSearch: {
    placeholder: 'Search for a place...',
    searchPlaceholder: 'Type to search places',
    emptyMessage: 'No places found'
  },
  seasonBadge: {
    summerTires: 'Summer tires',
    winterTires: 'Winter tires',
    allSeasonTires: 'All-season tires',
    unknown: 'Unknown'
  },
  tireMetaData: {
    frontTires: 'Front tires',
    rearTires: 'Rear tires',
    fuelEconomy: 'Fuel economy',
    wetGrip: 'Wet grip',
    noiseLevel: 'Noise level',
    studdedTires: 'Studded tires',
    euTireLabel: 'EU tire label'
  },
  tireSelection: {
    perTire: 'Per tire',
    total: 'Total',
    pcs: 'pcs',
    select: 'Select',
    frontTires: 'Front tires',
    rearTires: 'Rear tires',
    fuelEconomy: 'Fuel economy',
    wetGrip: 'Wet grip',
    noiseLevel: 'Noise level',
    studdedTires: 'Studded tires',
    euTireLabel: 'EU tire label',
    frontTire: 'Front tire',
    rearTire: 'Rear tire'
  },
  tireOptions: {
    wetGrip: 'Wet grip',
    fuelEconomy: 'Fuel economy',
    noiseLevel: 'Noise level',
    tireCount: (count: number) => pluralizeWithValue({ value: count, word: 'tire' }),
    axleQuantities: (front: number, rear: number) => `${front} front · ${rear} rear`,
    frontAxle: 'Front',
    rearAxle: 'Rear',
    perTire: (price: string) => `${price} per tire`,
    recommended: 'Recommended',
    selectedByCustomer: 'Selected by customer',
    priority: (value: number) => `Priority ${value}`,
    noOptions: 'No tire options'
  },
  select: {
    selectOption: 'Select option',
    noOptions: 'No option found',
    search: 'Search...'
  },
  chooseFromYourAddresses: 'Choose from your addresses?',
  couldNotFindAddress: 'We couldn’t find the address. Please check for typos or missing details and try again!',
  addressNotFound: 'No addresses found',
  tryAgainWithStreetNumber: 'We couldn’t find the address. Please check for typos or missing details and try again!',
  chooseFromAnotherAddress: 'Choose from another address?',
  forbiddenAccess: 'Forbidden access',
  youDoNotHavePermission: 'You do not have permission to access this application',
  pageNotFoundHeading: 'Page not found',
  pageNotFoundMessage:
    ' The page you are looking for has either been moved or no longer exists. If we have linked incorrectly, we would be very happy if you send us a message.',
  goBack: 'Go Back',
  goHome: 'Go Home',
  enterStartAddress: 'Enter start address',
  enterEndAddress: 'Enter end address',
  selectStartAddress: 'Start address',
  selectEndAddress: 'End address',
  chooseFromExistingAddress: 'Choose from existing address?',
  chooseNewStartAddress: 'Choose new start address?',
  chooseNewEndAddress: 'Choose new end address?',
  email: 'Email',
  password: 'Password',
  logIn: 'Sign in',
  accessDeniedLoginMessage:
    'Access denied. If you believe this is a mistake or require permission, please contact support.',
  enterCode: 'Enter code',
  confirm: 'Confirm',
  capacityContributionAddresses: {
    enterStartAddress: 'Enter start address',
    enterEndAddress: 'Enter end address',
    selectStartAddress: 'Start address',
    selectEndAddress: 'End address',
    chooseFromExistingAddress: 'Pick a saved address',
    chooseNewStartAddress: 'Enter another start address',
    chooseNewEndAddress: 'Enter another end address',
    sameAsStartAddress: 'Same as start address'
  },
  addressTranslations: {
    chooseFromYourAddresses: 'Choose from your addresses?',
    couldNotFindAddress: 'We couldn’t find the address. Please check for typos or missing details and try again!',
    addressNotFound: 'No addresses found',
    tryAgainWithStreetNumber: 'We couldn’t find the address. Please check for typos or missing details and try again!',
    chooseFromAnotherAddress: 'Choose from another address?'
  },
  myRouteOnly: 'My route only',
  youDontHaveWorkerId: "You don't have a worker id associated with your profile.",
  smsLoginTranslations: {
    phoneNumber: 'Phone number',
    enterNumberToReceiveVerificationCode: 'Enter number to receive a verification code',
    resend: 'Resend',
    sendCode: 'Send code',
    didntReceiveSms: "Didn't receive sms?",
    enterCode: 'Enter code',
    confirm: 'Confirm',
    sentTo: 'Sent to',
    changeNumber: 'Change number',
    sendNewCode: 'Send new code',
    accessDeniedLoginMessage:
      'Access denied. If you believe this is a mistake or require permission, please contact support.',
    chooseAccount: 'Choose an account',
    chooseAccountDescription: 'This phone number is linked to more than one account. Select the one you want to use.',
    back: 'Back',
    personalAccountPrivate: 'Private',
    personalAccountBusiness: 'Business'
  },
  passkeyLoginTranslations: {
    signInWithPasskey: 'Sign in with passkey',
    passkeyNotSupported: 'Passkeys are not supported in your browser. Please use phone or email login instead.',
    passkeyAuthenticationFailed: 'Passkey authentication failed. Please try again.',
    accessDeniedLoginMessage:
      'Access denied. If you believe this is a mistake or require permission, please contact us at hei@noddi.no.'
  },
  wheelMeasurements: {
    summerTires: 'Summer tires',
    winterTires: 'Winter tires',
    leftFront: 'Left front',
    rightFront: 'Right front',
    leftRear: 'Left rear',
    rightRear: 'Right rear',
    measuredAt: 'Measured at',
    missingMeasurements: 'Missing measurements',
    inAGoodCondition: 'In good condition',
    soonWornOut: 'Soon worn out',
    wornOut: 'Worn out',
    treadDepthCondition: 'Tread depth condition',
    treadDepth: 'Tread depth',
    legalMinimum: 'Legal minimum',
    recommendedMinimum: 'Recommended minimum',
    frontAxleShort: 'F',
    rearAxleShort: 'R',
    noMeasurements: 'No measurements',
    allSeasonTires: 'All season tires',
    unknown: 'Unknown',
    showMore: 'Show more',
    showLess: 'Show less'
  },
  pickingListTranslations: {
    missingStorageArea: 'Missing storage area',
    missingStorageLocation: 'Missing storage unit',
    noWheelSetsFound: 'No wheel sets found',
    tryChoosingAnotherDateOrStorageFacility: 'Try choosing another date or tire hotel',
    multipleRoutesFound: 'Multiple routes found',
    atLoadingDock: 'At loading dock',
    inTransit: 'In transit',
    refresh: 'Refresh'
  },
  registrationTranslations: {
    thisFieldIsRequired: 'This field is required',
    thisFieldMustBeAtLeast2CharactersLong: 'This field must be at least 2 characters long',
    mustProvideAValidEmailAddress: 'Must provide a valid email address',
    passwordsMustMatch: 'Passwords must match',
    thePasswordMustBeAtLeast8CharactersLong: 'The password must be at least 8 characters long',
    firstName: 'First name',
    surname: 'Surname',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm password',
    createUser: 'Create account',
    verificationExpired:
      'Your phone verification has expired. Verify your number again to continue. Your details will be kept.',
    verifyPhoneAgain: 'Verify phone number again',
    userInformation: 'User information',
    licensePlateNumber: 'License plate number (optional)',
    addLicensePlate: 'Add license plate',
    removeLicensePlate: 'Remove license plate',
    possibleExistingAccount: 'These details match an existing account. A new account will not sign you into it.',
    createNewAccount: 'Create new account',
    emailAlreadyHasAccount: 'This email already has an account. Sign in with your password, or reset it.',
    emailSignInLinkPrompt: 'We found an account for this email. We can send a sign-in link to it.',
    sendSignInLink: 'Email me a sign-in link',
    signInLinkSent: 'Check your email for a link to sign in.',
    signIn: 'Sign in',
    forgotPassword: 'Forgot password?',
    passwordResetSuccess: (email: string) => `We've sent a link to reset your password to ${email}.`,
    accessDeniedLoginMessage:
      'Access denied. If you believe this is a mistake or require permission, please contact support.'
  },
  profileUpdatedSuccessfully: 'Profile updated successfully',
  firstName: 'First name',
  lastName: 'Last name',
  phoneNumber: 'Phone number',
  save: 'Save',
  required: 'Required',
  addressValidationTranslations: {
    nameIsRequired: 'Name is required',
    streetNameIsRequired: 'Street name is required',
    streetNumberIsRequired: 'Street number is required',
    zipCodeIsRequired: 'Zip code is required',
    cityIsRequired: 'City is required',
    countryIsRequired: 'Country is required',
    latitudeIsRequired: 'Latitude is required',
    longitudeIsRequired: 'Longitude is required',
    descriptionIsRequired: 'Description is required',
    placeTypeIsRequired: 'Place type is required'
  },

  summer: 'Summer',
  winter: 'Winter',
  allSeason: 'All Season',
  tires: 'Tires',
  licenseArea: 'License area',
  serviceDepartment: 'Service department',
  fromDate: 'From date',
  date: 'Date',

  userGroup: 'Customer',
  serviceTime: 'Service time',
  serviceArea: 'Service area',
  timeWindow: 'Time slot',
  price: 'Price',
  status: 'Status',
  amount: 'Amount',
  discount: 'Discount',
  used: 'Used',
  activated: 'Activated',
  customerComments: 'Customer comment',
  adminComments: 'Admin comment',
  workerComments: 'Worker comment',

  canceled: 'Canceled',
  startsTooLate: 'Starts too late',
  cars: 'Cars',
  unableToComplete: 'Unable to complete',
  thisRouteIsNotValid: 'Invalid route',
  break: 'break',
  booking: 'Booking',
  waitingTime: 'Waiting time',
  tabs: {
    more: 'More'
  },
  customersList: {
    personalCustomers: 'Personal customers',
    organizations: 'Organizations',
    groups: 'Groups'
  },

  detailPage: {
    moreActions: 'More actions',
    created: 'Created',
    updated: 'Updated',
    openSidebar: 'Open sidebar',
    closeSidebar: 'Close sidebar'
  },
  cardHeader: {
    countOf: (count: number, total: number) => `${count} of ${total}`
  },
  stepProgress: {
    stepOf: (step: number, total: number) => `Step ${step} of ${total}`
  },
  listCard: {
    // Named after the card, so two list cards on one page never share a control name.
    search: (title: string) => `Search ${title.toLowerCase()}`,
    closeSearch: (title: string) => `Close ${title.toLowerCase()} search`,
    searchPlaceholder: 'Search',
    noItems: 'Nothing here yet',
    noMatches: (query: string) => `No matches for "${query}"`,
    openHint: 'Press Enter to open'
  },
  infoCard: {
    edit: 'Edit',
    // Named after the card or row, so several info cards on one page never share a control name.
    editCard: (title: string) => `Edit ${title.toLowerCase()}`,
    add: (label: string) => `Add ${label.toLowerCase()}`,
    copy: (label: string) => `Copy ${label.toLowerCase()}`,
    copied: 'Copied',
    copyFailed: 'Failed to copy to clipboard',
    opensInNewTab: '(opens in a new tab)'
  },
  notes: {
    title: 'Notes',
    add: 'Add',
    noNotes: 'No notes yet',
    pinned: 'Pinned',
    pinnedNotes: 'Pinned notes',
    pin: 'Pin note',
    unpin: 'Unpin note',
    viewAll: (count: number) => `View all ${count}`,
    category: 'Category',
    note: 'Note',
    writeNote: 'Write a note',
    contentRequired: 'Write something first',
    saveNote: 'Save note',
    save: 'Save',
    cancel: 'Cancel',
    moreActions: 'More actions',
    edit: 'Edit',
    delete: 'Delete',
    deleteTitle: 'Delete note',
    deleteDescription: 'The note is removed for everyone. This cannot be undone.',
    attachments: 'Attachments',
    addAttachments: 'Add files',
    takePhoto: 'Take photo',
    capturePhotoDescription: 'The photo is attached to the note when you save it.',
    attachmentHint: 'Images (JPEG, PNG, WebP, AVIF, GIF) or documents (PDF, TXT, CSV, Word, Excel). Max 10 MB each.',
    attachmentTooLarge: 'Each file must be 10 MB or smaller.',
    attachmentTypeNotAllowed: 'That file type is not allowed on notes.',
    attachmentUploadFailed: 'Upload failed',
    retryUpload: 'Retry upload',
    removeAttachment: 'Remove attachment',
    attachmentsRemovedOnSave: (files: string) => `${files} will be removed when you save.`,
    undoRemoval: 'Undo',
    uploadingAttachments: 'Uploading attachments…',
    noteSavedFinishUploads: 'The note is saved. Finish the upload, or close to keep the note without the files.',
    unsentFilesTitle: 'Files did not upload',
    unsentFilesDescription:
      'The note is saved without its files. Keep the note with just its text, or delete it entirely.',
    keepNote: 'Keep note',
    openImage: (filename: string) => `Open image ${filename}`,
    downloadDocument: (filename: string) => `Download ${filename}`,
    visibility: 'Visible to',
    visibilityCustomer: 'Visible to customer',
    visibilityWorker: 'Visible to worker',
    visibilityStaffOnly: 'Staff only',
    visibilityHelper: 'Choose who can see this note. Off for both keeps it staff-only.'
  },
  webcam: {
    cancel: 'Cancel',
    capture: 'Capture photo',
    starting: 'Starting camera…',
    preview: 'Camera preview',
    unsupportedTitle: 'Camera is not available',
    unsupportedBody: 'The camera needs a secure (https) connection. Add the photo as a file instead.',
    blockedTitle: 'Camera access is blocked',
    blockedBody: 'Allow camera access for this site and try again.',
    missingTitle: 'No camera found',
    missingBody: 'Connect a camera and try again.',
    busyTitle: 'Camera is in use',
    busyBody: 'Another app is using the camera. Close it and try again.',
    tryAgain: 'Try again',
    captureFailed: 'Could not read a frame from the camera. Try again.'
  },
  table: {
    rowsPerPage: 'Page size',
    of: 'of',
    selected: 'selected',
    clearFilters: 'Clear filters',
    totalOf: 'Total of',
    pageSize: 'Page size'
  },
  orderSummary: {
    total: 'Total',
    reconciliationWarning:
      'The recorded total and VAT do not match the current billable services. Review the order and settlement before collecting payment or making a billing correction. Recorded balances have not been changed.',

    discount: 'Discount',
    refund: 'Refund',
    VAT: 'VAT',
    vat: 'VAT',
    other: 'Other',
    refunded: 'Refunded',
    unknownLine: 'Unknown line',
    order: 'Order',
    searchOrderLines: 'Search order lines',
    noOrderLines: 'No order lines yet',
    noOrderLinesMatch: (query: string) => `No order lines match "${query}"`
  },
  priceListDiscountLabel: defaultPriceListDiscountLabelTexts,
  priceListContextBanner: defaultPriceListContextBannerTexts,
  tireDimensionInput: {
    width: 'Width',
    profile: 'Profile',
    rim: 'Rim',
    widthAriaLabel: 'Width in millimeters',
    profileAriaLabel: 'Profile in percent of the width',
    rimAriaLabel: 'Rim diameter in inches',
    construction: 'Construction',
    constructionTooltip: 'How the tire is built. R is radial and most common. D is diagonal. B is bias-belted.',
    widthOutsideTypicalRange: (min: number, max: number) => `The width is usually between ${min} and ${max} mm.`,
    profileOutsideTypicalRange: (min: number, max: number) => `The profile is usually between ${min} and ${max}.`,
    rimOutsideTypicalRange: (min: number, max: number) =>
      `The rim diameter is usually between ${min} and ${max} inches.`
  },
  tireHotelAtOtherDepartmentDialog: {
    title: 'Tire hotel at another location',
    description: (departmentName: string) =>
      `This car has a tire hotel at ${departmentName}. Book there so the stored wheels are at the same location as the service.`,
    switchTo: (departmentName: string) => `Switch to ${departmentName}`,
    continueHere: 'Continue here'
  },

  completed: 'Completed',
  cancelled: 'Cancelled',
  assigned: 'Assigned',
  confirmed: 'Confirmed',
  draft: 'Draft',

  yourOrder: 'Your order',
  discounts: 'Discounts',
  other: 'Other',
  total: 'Total',
  vat: 'VAT',
  seeBooking: 'See booking',
  summary: 'Summary',
  successFullyCreated: 'Successfully created!',
  successFullyUpdated: 'Successfully updated!',
  successFullyDeleted: 'Successfully deleted!',
  successfulOperation: 'Operation completed successfully',
  unknown: 'Unknown',
  language: 'Language',

  regNumberText: {
    add: 'Add',
    placeholder: 'Search for reg. number',
    invalidLicensePlate: 'Invalid license plate number',
    alreadyExists: 'The car with this license plate number has already been added'
  },

  timePicker: {
    jumpToDateTriggerText: 'Jump to date',
    loading: 'Loading',
    nextButton: 'Later',
    prevButton: 'Earlier',
    soldOut: 'Sold out',
    today: 'Today',
    tomorrow: 'Tomorrow',
    full: 'Full',
    // Fits a phone day tab, a fifth of the row, where "2 available" ran into the next tab. Reads the same
    // for one and many in English; the customer app pluralizes nb/se.
    availableCount: (count: number) => `${count} open`,
    earliestDeliveryDialogTitle: "Can't go back further",
    earliestDeliveryDialogMessage: 'This is the earliest date we can come to you',
    earliestAvailableDate: 'Earliest available date',
    earliestDeliveryReasons: {
      wheel_storage_inventory_lead_time: 'Your tires are in storage and need time to be prepared before delivery.',
      wheel_pickup_at_other_address_lead_time:
        'Your tires are at another address and need time to be collected before delivery.',
      default: "We're working hard to fit you in as soon as possible!"
    },
    latestDeliveryDialogTitle: "Can't go forward further",
    latestDeliveryDialogMessage: 'This is the latest date currently open for booking.',
    latestAvailableDate: 'Latest available date as of now',
    latestDeliveryExplanation: 'Later dates will open up as we get closer. Check back soon for more options!'
  },

  errors: {
    title: "We're sorry, an error has occurred",
    description: 'Please contact us to solve the issue',
    tryAgain: 'Try again'
  },

  chatAgent: {
    unavailableMaintenance:
      'The assistant is temporarily unavailable while we carry out maintenance. Please try again shortly.',
    unavailable: 'The assistant is temporarily unavailable. Please try again later.',
    sessionAlreadyStarted: 'This chat turn already started. Send a new message.',
    sessionExpired: 'Chat session expired. Please send your message again.',
    permissionDenied: 'You do not have access to the chat agent.',
    feedbackNotRecorded: 'Could not save your feedback. Please try again.'
  },

  duration: {
    pickADuration: 'Pick a duration',
    duration: 'Duration',
    quickPick: 'Quick pick',
    custom: 'Custom',
    done: 'Done',
    close: 'Close',
    unitDayShort: 'd',
    unitDayLong: 'days',
    unitDaySingular: 'day',
    unitHourShort: 'h',
    unitHourLong: 'hours',
    unitHourSingular: 'hour',
    unitMinuteShort: 'min',
    unitMinuteLong: 'minutes',
    unitMinuteSingular: 'minute',
    unitSecondShort: 's',
    unitSecondLong: 'seconds',
    unitSecondSingular: 'second'
  }
};

export type DurationTranslations = typeof commonTexts.duration;
export type TireHotelAtOtherDepartmentDialogTexts = typeof commonTexts.tireHotelAtOtherDepartmentDialog;
